/**
 * DecorateInteraction - 자식 컴포넌트에 default / hover / focus / active 상태별 스타일을 적용하는 래퍼
 * variant·color 조합으로 강도와 색상을 제어합니다.
 */

import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

// ============================================
// 타입 정의
// ============================================

export interface DecorateInteractionProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant: "light" | "normal" | "strong";
  color: "neutral" | "blue" | "white";
  /** true면 래퍼 없이 자식 요소에 스타일을 직접 병합 */
  asChild?: boolean;
  className?: string;
  children: React.ReactNode;
}

// ============================================
// 스타일 (CVA)
// Figma Decorate/Interaction 기준
// Light = Normal × 0.75, Strong = Normal × 1.5
// ============================================

const decorateInteractionVariants = cva(
  "relative outline-none cursor-pointer rounded-[0.375rem] before:absolute before:inset-0 before:rounded-[inherit] before:pointer-events-none before:transition-colors",
  {
    variants: {
      variant: {
        light: "",
        normal: "",
        strong: "",
      },
      color: {
        neutral: "",
        blue: "",
        white: "",
      },
    },
    compoundVariants: [
      // ----- neutral (bg-neutral-950) -----
      {
        variant: "light",
        color: "neutral",
        className:
          "hover:before:bg-neutral-950/6 focus-within:before:bg-neutral-950/[.09] active:before:bg-neutral-950/12",
      },
      {
        variant: "normal",
        color: "neutral",
        className:
          "hover:before:bg-neutral-950/8 focus-within:before:bg-neutral-950/12 active:before:bg-neutral-950/16",
      },
      {
        variant: "strong",
        color: "neutral",
        className:
          "hover:before:bg-neutral-950/12 focus-within:before:bg-neutral-950/[.18] active:before:bg-neutral-950/24",
      },
      // ----- blue (bg-blue-500) -----
      {
        variant: "light",
        color: "blue",
        className:
          "hover:before:bg-blue-500/6 focus-within:before:bg-blue-500/[.09] active:before:bg-blue-500/12",
      },
      {
        variant: "normal",
        color: "blue",
        className:
          "hover:before:bg-blue-500/8 focus-within:before:bg-blue-500/12 active:before:bg-blue-500/16",
      },
      {
        variant: "strong",
        color: "blue",
        className:
          "hover:before:bg-blue-500/12 focus-within:before:bg-blue-500/[.18] active:before:bg-blue-500/24",
      },
      // ----- white (bg-white) -----
      {
        variant: "light",
        color: "white",
        className:
          "hover:before:bg-white/6 focus-within:before:bg-white/[.09] active:before:bg-white/12",
      },
      {
        variant: "normal",
        color: "white",
        className:
          "hover:before:bg-white/8 focus-within:before:bg-white/12 active:before:bg-white/16",
      },
      {
        variant: "strong",
        color: "white",
        className:
          "hover:before:bg-white/12 focus-within:before:bg-white/[.18] active:before:bg-white/24",
      },
    ],
    defaultVariants: {
      variant: "normal",
      color: "neutral",
    },
  },
);

// ============================================
// 컴포넌트
// ============================================

export function DecorateInteraction({
  variant = "normal",
  color = "neutral",
  asChild = false,
  className,
  children,
  ...rest
}: DecorateInteractionProps) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(decorateInteractionVariants({ variant, color }), className)}
      {...rest}
    >
      {children}
    </Comp>
  );
}
